import type { GlowstickAttack } from '../../../data/full/superfan';
import { angleOf } from '../../../shared/angle';
import { clamp } from '../../../shared/scalar';
import { beatOfTick, isBeatTick } from '../../../shared/tempo';
import { fan } from '../../attacks/fan';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy, keepWhere, touches } from '../../effects';
import type { CoreState, EnemyState, PlayerState, PropState, Vec2 } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { layZone } from '../../zones';
import { ownerOf } from '../../props';

export const GLOWSTICK = 'glowstick';

export const glowstick: AttackModule<GlowstickAttack> = {
  reads: [
    'attackCooldownMul',
    'attackRadiusMul',
    'damageMul',
    'pierceAdd',
    'projectileCountAdd',
    'projectileSpeedMul',
  ],

  step({ state, tempo }, player, attack, input) {
    if (!pullTrigger(player, input.fire, attack.cooldownTicks)) {
      return;
    }
    const beat = beatOfTick(state.tick, tempo);
    const count = Math.max(1, Math.floor(statValue(player, 'projectileCountAdd', 1)));
    const far = statValue(player, 'projectileSpeedMul', 1);
    const damage = statValue(player, 'damageMul', attack.zone.damage ?? 0);
    const hits = Math.max(0, Math.floor(statValue(player, 'pierceAdd', 0)));
    const { width, height } = state.arena;
    fan(player.aim, count, attack.spreadRadians).forEach((way, index) => {
      const thrown = attack.throws[beat % attack.throws.length] ?? 0;
      const distance = aimedAt(state.enemies, player, way, attack, far) ?? thrown * far;
      const toX = clamp(player.x + way.x * distance, 0, width);
      const toY = clamp(player.y + way.y * distance, 0, height);
      const stick: PropState = {
        id: state.nextEntityId,
        kind: GLOWSTICK,
        playerId: player.id,
        phase: 'flying',
        x: player.x,
        y: player.y,
        prevX: player.x,
        prevY: player.y,
        vx: (toX - player.x) / attack.flightTicks,
        vy: (toY - player.y) / attack.flightTicks,
        damage,
        ticksLeft: attack.flightTicks,
        hitsLeft: hits,
        ...(hits > 0 ? { hitIds: [] } : {}),
        rank: beat + index,
      };
      state.nextEntityId += 1;
      (state.props ??= []).push(stick);
    });
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state } = ctx;
    const props = state.props;
    if (props === undefined) {
      return;
    }
    let landed = false;
    for (const prop of props) {
      if (prop.kind !== GLOWSTICK) {
        continue;
      }
      prop.x += prop.vx;
      prop.y += prop.vy;
      prop.ticksLeft -= 1;
      bonk(ctx, prop);
      if (prop.ticksLeft <= 0) {
        plantStick(ctx, ownerOf(state, prop), prop, { x: prop.vx, y: prop.vy });
        landed = true;
      }
    }
    if (landed) {
      keepWhere(props, (prop) => prop.kind !== GLOWSTICK || prop.ticksLeft > 0);
      if (props.length === 0) {
        delete state.props;
      }
    }
  },

  reach(attack, player) {
    return statValue(player, 'projectileSpeedMul', Math.max(...attack.throws));
  },
};

export function frontRowPower(
  attack: Pick<GlowstickAttack, 'frontRow'>,
  core: Pick<CoreState, 'x' | 'y' | 'radius'>,
  at: Vec2,
): number {
  const dx = at.x - core.x;
  const dy = at.y - core.y;
  const gap = Math.max(0, Math.sqrt(dx * dx + dy * dy) - core.radius);
  const { reach, bonus } = attack.frontRow;
  return 1 + bonus * Math.max(0, 1 - gap / reach);
}

function aimedAt(
  enemies: readonly EnemyState[],
  player: PlayerState,
  way: Vec2,
  attack: GlowstickAttack,
  far: number,
): number | undefined {
  const longest = Math.max(...attack.throws) * far;
  let nearest: number | undefined;
  for (const enemy of enemies) {
    const dx = enemy.x - player.x;
    const dy = enemy.y - player.y;
    const along = dx * way.x + dy * way.y;
    const across = Math.abs(dy * way.x - dx * way.y);
    if (
      enemy.hp > 0 &&
      along > 0 &&
      along <= longest &&
      across <= enemy.radius + attack.zone.radius / 2 &&
      (nearest === undefined || along < nearest)
    ) {
      nearest = along;
    }
  }
  return nearest;
}

// Pierce lets a stick in flight hit that many bad vibes it flies through, each once.
function bonk(ctx: StepContext, stick: PropState): void {
  if (stick.hitsLeft <= 0) {
    return;
  }
  const { state } = ctx;
  const hitIds = (stick.hitIds ??= []);
  const radius = glowstickOf(ctx, ownerOf(state, stick)).stickRadius;
  for (const enemy of state.enemies) {
    if (stick.hitsLeft <= 0) {
      return;
    }
    if (enemy.hp > 0 && !hitIds.includes(enemy.id) && touches(enemy, stick, radius)) {
      hitIds.push(enemy.id);
      stick.hitsLeft -= 1;
      hurtEnemy(state, enemy, stick.damage, stick.playerId, {
        x: stick.prevX,
        y: stick.prevY,
      });
    }
  }
}

export function glowstickOf(
  ctx: Pick<StepContext, 'content'>,
  owner: PlayerState,
): GlowstickAttack {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  if (attack?.kind !== 'glowstick') {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" throws no glowstick`);
  }
  return attack;
}

export function plantStick(ctx: StepContext, owner: PlayerState, at: Vec2, direction: Vec2): void {
  const attack = glowstickOf(ctx, owner);
  const zone = layZone(ctx, owner, attack.zone, {
    x: at.x,
    y: at.y,
    direction,
    power: statValue(owner, 'damageMul', 1) * frontRowPower(attack, ctx.state.core, at),
    radius: statValue(owner, 'attackRadiusMul', attack.zone.radius),
  });
  if (isBeatTick(ctx.state.tick, ctx.tempo)) {
    return;
  }
  for (const enemy of ctx.state.enemies) {
    if (enemy.hp > 0 && touches(enemy, zone, zone.radius)) {
      hurtEnemy(ctx.state, enemy, (attack.zone.damage ?? 0) * zone.power, owner.id, zone);
    }
  }
}
